import React, { useEffect, useState, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { Container, Row, Col, Modal, Button } from 'react-bootstrap';
import { injectIntl } from "react-intl";
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { far } from "@fortawesome/pro-regular-svg-icons";
import messages from './MessageCenter.messages';
import * as Sentry from "@sentry/react";

// components
import MessageCard from '../../components/MessageCard';
import MessageCardSkeleton from '../../components/MessageCardSkeleton';
import AlertDialog from '../../components/AlertDialog';
import MessageContent from '../../components/MessageContent';
import useMessages from "../../hooks/useMessages";
import useScreenSize from "../../hooks/useScreenSize";
import MessageSortDropdown from "../../components/MessageSortDropdown";
import useEnv from "../../hooks/useEnv";
import useUserAccount from "../../hooks/useUserAccount";

const MOBILE_WIDTH = 992;
const SKELETON_COUNT = 5;

const MessageCenter = (props) => {
    const lng = props.intl.formatMessage;
    const location = props.location; 
    const { fetchMessages, messageCenterMessages, setMessageUnread, sortMessages } = useMessages();
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [singleMessage, setSingleMessage] = useState({});
    const [showInModal, setShowInModal] = useState(false);
    const [loadingTickets, setLoadingTickets] = useState(false);
    const [showAlert, setShowAlert] = useState(false);
    const [alertMessage, setAlertMessage] = useState('');
    const [alertType, setAlertType] = useState('');
    const [sortOption, setSortOption] = useState('newest'); // state for sort option
    const messageRefs = useRef({});
    const fromJob = location.state?.fromJob;
    const targetUrl = fromJob ? `/job` : '/jobsearch';
    const [files, setFiles] = useState([]); // State for attachments
    const { width } = useScreenSize();
    const isMobile = width < MOBILE_WIDTH;
    const { isEnvTruthy } = useEnv();
    const { userIsLoggedIn } = useUserAccount();

    const MESSAGE_CENTER_ENABLED = isEnvTruthy('MESSAGECENTER');

    useEffect(() => {
        if (!MESSAGE_CENTER_ENABLED) {
            // redirect to jobsearch, if message center is not enabled in the environment
            const alert = { message: lng(messages.disabledInEnvironment), type: 'primary' };
            props.history.push({
                pathname: "/jobsearch",
                alert
            });
        } else if (userIsLoggedIn()) {
            // message center is enabled and user is logged in, get all user's messages
            setIsAuthenticated(true);
            if (props.maintenance) {
                showAlerts({
                    message: lng(messages.underMaintenance),
                    type: 'warning'
                });
            }
        }
    }, [props.maintenance]);

    useEffect(() => {
        if (location.state?.alert) {
            setAlertMessage(location.state.alert.message);
            setAlertType(location.state.alert.type);
            setShowAlert(true);
        }   
    }, [location.state]);

    /**
     * Update current message.
     */
    useEffect(() => {
        const sortedMessages = sortMessages(messageCenterMessages, sortOption);
        let selectedMessage = null;
        if (!isMobile && sortedMessages.length > 0) {
            selectedMessage = sortedMessages[0];
        }
        let currentMessageId = singleMessage?.id;
        if (!currentMessageId) {
            const queryParams = new URLSearchParams(location.search);
            currentMessageId = queryParams.get('messageId');
        }
        if (currentMessageId) {
            selectedMessage = messageCenterMessages.find(message => message.id === currentMessageId);
        }
        if (selectedMessage) {
            setSingleMessage(selectedMessage);
            setShowInModal(true);
            if (selectedMessage.current_status?.unread === 1) {
                void setMessageUnread(selectedMessage.id);
            }
            // Scroll to the active message card
            if (messageRefs.current[selectedMessage.id]) {
                messageRefs.current[selectedMessage.id].scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }
    }, [messageCenterMessages]);

    /**
     * Refresh all messages.
     */
    useEffect(() => {
        const getMessages = async () => {
            setSingleMessage({});
            setLoadingTickets(true);
            try {
                // Refresh storage
                const allStoredMessages = await fetchMessages();
                if (Array.isArray(allStoredMessages)) {
                    // Check if user has messages and set them
                    checkLengthAndSetMessages(allStoredMessages);
                }
            } catch (error) {
                Sentry.captureException(error);
                // show error message
                showAlerts({
                    message: lng(messages.errorOccurredTryAgain),
                    type: 'danger'
                });
            } finally {
                // Toggle skeleton loading off
                setLoadingTickets(false);
            }
        }
        if (!props.maintenance) {
            void getMessages();
        }
    }, [props.maintenance]);

    /**
     * Check passed message array and show them in the listing.
     * If messages are empty, show notification in alertDialog.
     *
     * @param {*} allMessages array of messages
     */
    const checkLengthAndSetMessages = (allMessages) => {
        if (allMessages.length > 0) {
            return;
        }
        // If user has no messages, show notification in alertDialog,
        // does the environment allow the use of chat?
        if (typeof process.env.REACT_APP_SHOW_CHAT !== 'undefined' && process.env.REACT_APP_SHOW_CHAT.toString() === '1') {
            showAlerts({
                message: lng(messages.noMessagesPrefix)+' "'+lng(messages.createNewMessage)+'" '+lng(messages.noMessagesSuffix),
                type: 'primary'
            });
        } else {
            showAlerts({
                message: lng(messages.noMessagesPrefix)+' "'+lng(messages.createNewMessage)+'" '+lng(messages.noMessagesSuffixWithoutChat),
                type: 'primary'
            });
        }
    }

    /**
     * Set alert message and type, and show alertDialog.
     *
     * @param {*} alert
     */
    const showAlerts = (alert) => {
        setAlertMessage(alert.message);
        setAlertType(alert.type);
        setShowAlert(true);
    }

    /**
     * Clear error messages and hide alertDialog.
     */
    const clearAlerts = () => {
        setAlertMessage('');
        setAlertType('');
        setShowAlert(false);
    }

    /**
     * Handle message card clicks.
     *
     * @param {string} messageId
     */
    const handleMessageCardClick = (messageId) => {
        clearAlerts();
        const clickedMessage = messageCenterMessages.find(message => message.id === messageId);
        setSingleMessage(clickedMessage ?? {});
        setShowInModal(true);
        if (clickedMessage.current_status?.unread === 1) {
            void setMessageUnread(clickedMessage.id);
        }
    }

    /**
     * Handle modal hide in mobile views.
     */
    const handleHideModal = () => {
        clearAlerts();
        if (fromJob) {
            props.history.push('/job');
        } else {
            setShowInModal(false);
        }
    }

    const handleNavigation = () => {
        props.history.push(targetUrl);
    }

    const skeletonElement = <>
        <Col xs={12} lg={4}>
            <MessageCardSkeleton count={SKELETON_COUNT} />
        </Col>
        <Col xs={12} lg={8} className="text-center">
            <MessageCardSkeleton count={1} />
        </Col>
    </>

    const messageListElement = <aside aria-label={lng(messages.messageList)}>
        <div className='scrollable-content' aria-labelledby="message-center-title">
            {sortMessages(messageCenterMessages, sortOption).map((message, index, items) => {
                const nextIsActive = index < items.length - 1 && singleMessage.id === items[index + 1].id;
                const isActive = message.id === singleMessage.id;
                return (
                    <div key={'message-' + message.id} ref={el => messageRefs.current[message.id] = el}>
                        <button
                            id={"message-" + message.id}
                            className="btn messagecard"
                            onClick={() => handleMessageCardClick(message.id)}
                        >
                            <MessageCard
                                cardIndex={index}
                                id={message.id}
                                status={message.current_status.status}
                                statusCode={message.jobstatus}
                                dueDate={message.due_date}
                                summary={message.summary}
                                invoiceNumber={message.jobid}
                                invoiceDpid={message.dpid}
                                creditor={message.creditor}
                                active={isActive}
                                isUnread={message.current_status.unread}
                                showAlerts={showAlerts}
                                {...props}
                            />
                        </button>
                        {/* do not show spacers before/after the chosen card*/}
                        <hr className={'mb-3 ' + (isActive || nextIsActive ? 'hide' : '') + " d-none d-sm-block"} aria-hidden="true" />
                    </div>
                );
            })}
        </div>
    </aside>

    if (!isAuthenticated) {
        return <></>;
    }

    return (
        <div className="content flex-fill">
            <Helmet id='job-helmet'>
                <title>{lng(messages.messageCenter)} | MyRopo</title>
            </Helmet>
            <Container id='message-center' className={'content flex-fill p-0 p-sm-4 pt-xl-4' + (showAlert ? ' alert-shown' : '')}>
                <Row className='pb-3 pt-3 d-none d-sm-none d-md-block'>
                    <Col xs={11}>
                        <button
                            className='btn text-start p-0'
                            aria-label={lng(messages.goBack)}
                            onClick={handleNavigation}>
                            <h5>
                                <FontAwesomeIcon icon={far.faArrowLeft} aria-hidden="true" /> &nbsp;
                                {lng(messages.goBack)}
                            </h5>
                        </button>
                    </Col>
                </Row>
                <Row className='mb-3 mt-3 mt-sm-0' aria-labelledby="message-center-title">
                    <Col xs={12} lg={4} >
                        <Row className='pe-3'>
                            <Col xs={8} sm={7} md={8} className='welcome-message pe-0'>
                                <h2 className='text-break ps-4 mb-0 ps-sm-0 ps-xl-4'
                                    id="message-center-title"
                                >
                                    {lng(messages.title)}</h2>
                            </Col>
                            {!props.maintenance && messageCenterMessages.length > 0 && (
                                <Col xs={4} sm={5} md={4} className='d-flex justify-content-end ps-0 pe-4 pe-sm-0'>
                                    <MessageSortDropdown
                                        sortOption={sortOption}
                                        onSortChange={setSortOption}
                                    />
                                </Col>
                            )}
                        </Row>
                    </Col>
                </Row>
                <div className='ps-4 pe-4 ps-sm-0 pe-sm-0'>
                    {showAlert && (
                        <AlertDialog
                            type={alertType}
                            message={alertMessage}
                        />
                    )}
                </div>
                <Row className='g-3'>
                    {loadingTickets && skeletonElement}
                    {!loadingTickets && !props.maintenance && messageCenterMessages.length > 0 && (
                        <>
                            <Col xs={12} lg={4} className='message-container'>
                                {messageListElement}
                            </Col>
                            <Col xs={12} lg={8} className="message-container">
                                {isMobile ? (
                                    <Modal
                                        className='d-block d-lg-none pb-0'
                                        show={showInModal}
                                        onHide={handleHideModal}
                                        dialogClassName='mobile-message-content'
                                        backdropClassName='mobile-message-content'
                                        contentClassName='mobile-message-content'
                                        backdrop={false}
                                    >
                                        <Modal.Header className='fixed-element-top ps-4 mobile-message-content'>
                                            <Button
                                                className="modal-back"
                                                onClick={handleHideModal}
                                                aria-label={lng(messages.goBack)}
                                            >
                                                <FontAwesomeIcon icon={far.faArrowLeft} aria-hidden="true" /> {lng(messages.goBack)}
                                            </Button>
                                            {showAlert && (
                                                <div className='pt-4'>
                                                    <AlertDialog
                                                        type={alertType}
                                                        message={alertMessage}
                                                    />
                                                </div>
                                            )}
                                        </Modal.Header>
                                        <Modal.Body className='p-0'>
                                            <MessageContent
                                                message={singleMessage}
                                                showAlerts={showAlerts}
                                                formId='message-center-form-mobile'
                                                files={files}
                                                setAttachments={setFiles}
                                            />
                                        </Modal.Body>
                                    </Modal>
                                ) : (
                                    <MessageContent
                                        message={singleMessage}
                                        showAlerts={showAlerts}
                                        formId='message-center-form-desktop'
                                        files={files}
                                        setAttachments={setFiles}
                                    />
                                )}
                            </Col>
                        </>
                    )}
                </Row>
            </Container>
        </div>
    );
};

MessageCenter.propTypes = {
    'intl': PropTypes.object.isRequired,
    'history': PropTypes.object,
    'location': PropTypes.object,
    'maintenance': PropTypes.bool.isRequired,
};

export default injectIntl(MessageCenter);
